"use client";

import type { SyntheticEvent } from "react";
import type { BlogAudio as Recording } from "@/lib/blog/types";

/** Starting one clip pauses every other on the page: two voices at once is noise. */
const pauseOthers = (e: SyntheticEvent<HTMLAudioElement>): void => {
  const playing = e.currentTarget;
  document.querySelectorAll("audio").forEach((a) => {
    if (a !== playing) a.pause();
  });
};

/**
 * A recording in a blog post (`BlogPost.audio`), with its caption already in
 * the reader's language. The browser's own player: it is keyboard- and
 * screen-reader-accessible as it stands, and `preload="none"` keeps a post
 * with six clips from downloading any until one is played.
 */
export function BlogAudio({ recording }: { recording: Recording }) {
  return (
    <figure>
      <div className="space-y-3 rounded-xl border border-border-subtle p-4">
        {recording.clips.map((clip) => (
          <div key={clip.src} className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {clip.label !== undefined && (
              // Above the player on a phone, where beside it the seek bar has no room left.
              <span className="w-full shrink-0 text-sm font-medium text-foreground-strong sm:w-28">{clip.label}</span>
            )}
            <audio controls preload="none" src={clip.src} onPlay={pauseOthers} className="h-10 min-w-0 flex-1">
              <a href={clip.src}>{clip.src}</a>
            </audio>
          </div>
        ))}
      </div>
      <figcaption className="mt-3 text-xs text-foreground-muted">{recording.caption}</figcaption>
    </figure>
  );
}
